/**
 * WebRTCManager
 * Manages peer-to-peer WebRTC connections for audio/video calls.
 * Supports both 1:1 and mesh-topology group calls (up to ~6 peers).
 */

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

interface PeerState {
  pc: RTCPeerConnection;
  remoteStream: MediaStream;
  iceCandidateBuffer: RTCIceCandidateInit[];
  remoteDescriptionSet: boolean;
}

export type ConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';

export class WebRTCManager {
  private peers: Map<string, PeerState> = new Map();
  private localStream: MediaStream | null = null;
  private iceServers: RTCIceServer[];

  // Callbacks - set by CallContext
  public onRemoteStream: ((userId: string, stream: MediaStream) => void) | null = null;
  public onRemoteStreamRemoved: ((userId: string) => void) | null = null;
  public onIceCandidate: ((targetUserId: string, candidate: RTCIceCandidateInit) => void) | null = null;
  public onConnectionStateChange: ((userId: string, state: ConnectionState) => void) | null = null;

  constructor(iceServers?: RTCIceServer[]) {
    this.iceServers = iceServers || DEFAULT_ICE_SERVERS;
  }

  // ────────── Media Stream ──────────

  async getLocalStream(audio: boolean, video: boolean): Promise<MediaStream> {
    if (this.localStream) {
      // Update existing tracks if needed
      this.localStream.getAudioTracks().forEach(t => (t.enabled = audio));
      this.localStream.getVideoTracks().forEach(t => (t.enabled = video));
      return this.localStream;
    }

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: audio ? { echoCancellation: true, noiseSuppression: true } : false,
        video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' } : false,
      });
      return this.localStream;
    } catch (err) {
      console.error('Failed to get local media stream:', err);
      throw err;
    }
  }

  stopLocalStream(): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
  }

  toggleAudio(enabled: boolean): void {
    this.localStream?.getAudioTracks().forEach(t => (t.enabled = enabled));
  }

  toggleVideo(enabled: boolean): void {
    this.localStream?.getVideoTracks().forEach(t => (t.enabled = enabled));
  }

  getLocalMediaStream(): MediaStream | null {
    return this.localStream;
  }

  // ────────── Peer Connection Lifecycle ──────────

  private createPeerConnection(targetUserId: string): PeerState {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const remoteStream = new MediaStream();

    const peerState: PeerState = {
      pc,
      remoteStream,
      iceCandidateBuffer: [],
      remoteDescriptionSet: false,
    };

    // Add local tracks to the connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.localStream!);
      });
    }

    // Handle remote tracks
    pc.ontrack = (event) => {
      event.streams[0]?.getTracks().forEach(track => {
        remoteStream.addTrack(track);
      });
      this.onRemoteStream?.(targetUserId, remoteStream);
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.onIceCandidate?.(targetUserId, event.candidate.toJSON());
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      this.onConnectionStateChange?.(targetUserId, pc.connectionState as ConnectionState);

      if (pc.connectionState === 'failed') {
        console.warn(`WebRTC connection to ${targetUserId} failed, attempting ICE restart`);
        pc.restartIce();
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected') {
        // Give it a few seconds before declaring failure
        setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') {
            this.onConnectionStateChange?.(targetUserId, 'disconnected');
          }
        }, 3000);
      }
    };

    this.peers.set(targetUserId, peerState);
    return peerState;
  }

  /**
   * Create and send an SDP offer to a target peer (caller side)
   */
  async createOffer(targetUserId: string): Promise<RTCSessionDescriptionInit> {
    // Remove any existing connection to this peer
    this.removePeer(targetUserId);

    const peer = this.createPeerConnection(targetUserId);

    const offer = await peer.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });
    await peer.pc.setLocalDescription(offer);

    return offer;
  }

  /**
   * Handle an incoming SDP offer and return an answer (callee side)
   */
  async handleOffer(fromUserId: string, sdp: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    // Remove any existing connection to this peer
    this.removePeer(fromUserId);

    const peer = this.createPeerConnection(fromUserId);

    await peer.pc.setRemoteDescription(new RTCSessionDescription(sdp));
    peer.remoteDescriptionSet = true;

    // Flush buffered ICE candidates
    await this.flushIceCandidateBuffer(fromUserId);

    const answer = await peer.pc.createAnswer();
    await peer.pc.setLocalDescription(answer);

    return answer;
  }

  /**
   * Handle an SDP answer from a peer (caller side)
   */
  async handleAnswer(fromUserId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const peer = this.peers.get(fromUserId);
    if (!peer) {
      console.warn(`No peer connection for ${fromUserId} to set answer`);
      return;
    }

    await peer.pc.setRemoteDescription(new RTCSessionDescription(sdp));
    peer.remoteDescriptionSet = true;

    // Flush buffered ICE candidates
    await this.flushIceCandidateBuffer(fromUserId);
  }

  /**
   * Add an ICE candidate from a remote peer
   */
  async addIceCandidate(fromUserId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const peer = this.peers.get(fromUserId);
    if (!peer) {
      console.warn(`No peer connection for ${fromUserId}, ignoring ICE candidate`);
      return;
    }

    if (!peer.remoteDescriptionSet) {
      // Buffer the candidate until remote description is set
      peer.iceCandidateBuffer.push(candidate);
      return;
    }

    try {
      await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.warn(`Failed to add ICE candidate from ${fromUserId}:`, err);
    }
  }

  private async flushIceCandidateBuffer(userId: string): Promise<void> {
    const peer = this.peers.get(userId);
    if (!peer) return;

    for (const candidate of peer.iceCandidateBuffer) {
      try {
        await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.warn(`Failed to flush ICE candidate for ${userId}:`, err);
      }
    }
    peer.iceCandidateBuffer = [];
  }

  // ────────── Cleanup ──────────

  removePeer(userId: string): void {
    const peer = this.peers.get(userId);
    if (peer) {
      peer.pc.close();
      this.peers.delete(userId);
      this.onRemoteStreamRemoved?.(userId);
    }
  }

  closeAllConnections(): void {
    for (const [userId, peer] of this.peers) {
      peer.pc.close();
      this.onRemoteStreamRemoved?.(userId);
    }
    this.peers.clear();
    this.stopLocalStream();
  }

  // ────────── Getters ──────────

  getRemoteStream(userId: string): MediaStream | null {
    return this.peers.get(userId)?.remoteStream || null;
  }

  getAllRemoteStreams(): Map<string, MediaStream> {
    const streams = new Map<string, MediaStream>();
    for (const [userId, peer] of this.peers) {
      streams.set(userId, peer.remoteStream);
    }
    return streams;
  }

  getPeerIds(): string[] {
    return Array.from(this.peers.keys());
  }
}
